/** Same-origin JSON API client. State-changing calls carry the CSRF header the server requires. */
import { afterWrite } from './analytics';
import { viewAs } from './impersonation';
import { trialPreview } from './preview';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly body?: unknown,
  ) {
    super(message);
  }
}

type Listener = () => void;
const unauthorized = new Set<Listener>();
/** Called when any request finds the session has ended (401). */
export function onUnauthorized(fn: Listener): () => void {
  unauthorized.add(fn);
  return () => unauthorized.delete(fn);
}

const lostApp = new Set<Listener>();
/**
 * Called when a request finds the person no longer has the app it belongs to (29 Oct 2026: the free trial ended, or
 * an admin took access away): who they are is checked again, so the page moves to where they land.
 */
export function onAppAccessLost(fn: Listener): () => void {
  lostApp.add(fn);
  return () => lostApp.delete(fn);
}

export async function api<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const headers = new Headers(init.headers);
  const method = (init.method ?? 'GET').toUpperCase();
  if (method !== 'GET' && method !== 'HEAD') headers.set('X-Requested-With', 'attendance-pro');
  const as = viewAs.get();
  if (as) headers.set('X-Impersonate', as);
  // An admin's preview of Task Force's free trial (lib/preview.ts).
  if (trialPreview.on()) headers.set('X-Tf-Preview', 'trial');
  let body = init.body;
  if (init.json !== undefined) {
    headers.set('Content-Type', 'application/json');
    body = JSON.stringify(init.json);
  }
  const write = method !== 'GET' && method !== 'HEAD';
  let res: Response;
  try {
    res = await fetch(path, { ...init, method, headers, body, credentials: 'same-origin' });
  } catch (e) {
    // Offline or the server unreachable: analytics hears of the failed change.
    if (write) afterWrite(method, path, init.json, { code: 'NETWORK' });
    throw e;
  }
  const text = await res.text();
  const data = text
    ? (() => {
        try {
          return JSON.parse(text);
        } catch {
          return text;
        }
      })()
    : null;
  if (!res.ok) {
    const err = (data as { error?: { code?: string; message?: string } } | null)?.error;
    // A view-as tab whose impersonation ended (or expired): say so instead of showing the sign-in page.
    if (err?.code === 'IMPERSONATION_ENDED') {
      viewAs.clear();
      location.replace('/impersonate#ended');
    } else if (res.status === 401 && !path.startsWith('/api/auth/login')) unauthorized.forEach((f) => f());
    else if (res.status === 403 && (err?.code === 'NO_TF_ACCESS' || err?.code === 'NO_APP_ACCESS'))
      lostApp.forEach((f) => f());
    if (write) afterWrite(method, path, init.json, { code: err?.code ?? 'HTTP_' + res.status });
    throw new ApiError(
      res.status,
      err?.code ?? 'HTTP_' + res.status,
      err?.message ?? 'Something went wrong. Try again.',
      data,
    );
  }
  // A change the server accepted: its analytics event (lib/analytics.ts, 30 Oct 2026).
  if (write) afterWrite(method, path, init.json);
  return data as T;
}

export interface Me {
  id: string;
  email: string;
  displayName: string;
  role: 'HR_ADMIN' | 'TEAM_LEAD' | 'STAFF' | 'PROJECT_MANAGER' | 'NONE';
  mustChangePassword: boolean;
  /** In a view-as tab: the HR admin who is looking. */
  impersonatedBy?: { displayName: string; email: string };
  /** Task Force role; null or missing: no access. */
  tfRole?: 'owner' | 'admin' | 'member' | 'guest' | null;
  /** Access per app (18 Oct 2026): what they can open, what they manage, and where they land. */
  access?: Access;
}

export type AppKey = 'attendance' | 'clickup' | 'taskforce';
export interface Access {
  apps: Record<AppKey, boolean>;
  admin: Record<AppKey, boolean> & { super: boolean };
  settings: boolean;
  home: '/taskforce' | '/attendance' | '/clickup' | '/no-access';
}
